function ensureSubjectPrefix(subject, prefix) {
    const normalizedSubject = String(subject || "").trim() || "(no subject)";
    const prefixLower = prefix.toLowerCase();
    if (normalizedSubject.toLowerCase().startsWith(prefixLower)) {
        return normalizedSubject;
    }
    return `${prefix}${normalizedSubject}`;
}

function normalizeThreadSubject(value) {
    let subject = String(value || "").trim().toLowerCase();
    while (/^(re|fw|fwd)\s*:\s*/i.test(subject)) {
        subject = subject.replace(/^(re|fw|fwd)\s*:\s*/i, "").trim();
    }
    return subject;
}

function buildConversationMergeKey(item) {
    const subjectKey = normalizeThreadSubject(item?.subject);
    const emailKey = String(item?.contact_email || "").trim().toLowerCase();
    const nameKey = String(item?.contact_name || "").trim().toLowerCase();
    const counterparty = emailKey || nameKey;
    if (!subjectKey || !counterparty) {
        return "";
    }
    return `${counterparty}::${subjectKey}`;
}

function mergeConversations(payloads) {
    const byThreadId = new Map();

    payloads.forEach((payload) => {
        const rows = Array.isArray(payload?.conversations) ? payload.conversations : [];
        rows.forEach((item) => {
            const key = String(item?.thread_id || item?.id || "");
            if (!key) {
                return;
            }

            const existing = byThreadId.get(key);
            if (!existing) {
                byThreadId.set(key, item);
                return;
            }

            const existingTime = Date.parse(String(existing?.updated_at || "")) || 0;
            const nextTime = Date.parse(String(item?.updated_at || "")) || 0;
            if (nextTime >= existingTime) {
                byThreadId.set(key, { ...existing, ...item });
            }
        });
    });

    const deduped = Array.from(byThreadId.values());
    const mergedByKey = new Map();

    deduped.forEach((item) => {
        const threadId = String(item?.thread_id || item?.id || "");
        if (!threadId) {
            return;
        }

        const mergeKey = buildConversationMergeKey(item);
        if (!mergeKey) {
            mergedByKey.set(`thread:${threadId}`, {
                ...item,
                thread_id: threadId,
                thread_ids: [threadId],
                message_count: Number(item?.message_count || 0),
            });
            return;
        }

        const existing = mergedByKey.get(mergeKey);
        if (!existing) {
            mergedByKey.set(mergeKey, {
                ...item,
                thread_id: threadId,
                thread_ids: [threadId],
                message_count: Number(item?.message_count || 0),
            });
            return;
        }

        const existingTime = Date.parse(String(existing?.updated_at || "")) || 0;
        const nextTime = Date.parse(String(item?.updated_at || "")) || 0;
        const newest = nextTime >= existingTime ? item : existing;
        const threadIds = Array.from(new Set([...(existing.thread_ids || []), threadId]));

        mergedByKey.set(mergeKey, {
            ...existing,
            ...newest,
            thread_id: String(newest?.thread_id || newest?.id || threadIds[0] || ""),
            thread_ids: threadIds,
            message_count: Number(existing?.message_count || 0) + Number(item?.message_count || 0),
        });
    });

    return Array.from(mergedByKey.values()).sort((a, b) => {
        const aTime = Date.parse(String(a?.updated_at || "")) || 0;
        const bTime = Date.parse(String(b?.updated_at || "")) || 0;
        return bTime - aTime;
    });
}

function getContactDetailDisplayData({ contact, initialContact, relatedDeals }) {
    const primaryDeal = Array.isArray(relatedDeals) && relatedDeals.length > 0 ? relatedDeals[0] : null;
    const displayName = contact?.name ||
        (contact?.first_name && contact?.last_name
            ? `${contact.first_name} ${contact.last_name}`
            : "Contact");

    const displayCompany =
        contact?.company ||
        contact?.account ||
        initialContact?.company ||
        initialContact?.account ||
        "";

    const displayOwner =
        contact?.owner_name ||
        initialContact?.owner_name ||
        initialContact?.owner ||
        "";

    const displayPhone = contact?.phone || contact?.mobile || "";
    const displayEmail = contact?.email || "";
    const displayTitle = contact?.title || contact?.job_title || "";
    const displayDealType =
        contact?.deal_type ||
        contact?.dealType ||
        primaryDeal?.deal_type ||
        primaryDeal?.dealType ||
        "";

    const displayDealStage =
        contact?.stage ||
        contact?.stages ||
        contact?.status ||
        contact?.deal_stage ||
        contact?.dealStage ||
        initialContact?.stage ||
        initialContact?.stages ||
        initialContact?.status ||
        initialContact?.deal_stage ||
        initialContact?.dealStage ||
        primaryDeal?.stage ||
        primaryDeal?.stages ||
        primaryDeal?.status ||
        primaryDeal?.deal_stage ||
        primaryDeal?.dealStage ||
        "";

    const displayNextStep = contact?.next_step || contact?.nextStep || "";
    const displayLastTouch = contact?.last_touch || contact?.lastTouch || "";

    const rawDisplayDealValue =
        contact?.deal_value ??
        contact?.dealValue ??
        primaryDeal?.deal_value ??
        primaryDeal?.dealValue ??
        null;

    const displayDealValue = (() => {
        const hasRawValue = rawDisplayDealValue !== null && rawDisplayDealValue !== undefined && rawDisplayDealValue !== "";
        if (hasRawValue) {
            const parsed = Number(rawDisplayDealValue);
            return Number.isFinite(parsed) ? parsed : null;
        }

        if (primaryDeal?.amount_cents !== null && primaryDeal?.amount_cents !== undefined) {
            const parsed = Number(primaryDeal.amount_cents);
            return Number.isFinite(parsed) ? parsed / 100 : null;
        }

        return null;
    })();

    const displayDealValueFormatted =
        displayDealValue !== null && Number.isFinite(displayDealValue)
            ? displayDealValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
            : "";

    return {
        displayName,
        displayCompany,
        displayOwner,
        displayPhone,
        displayEmail,
        displayTitle,
        displayDealType,
        displayDealValueFormatted,
        displayDealStage,
        displayNextStep,
        displayLastTouch,
    };
}

export {
    ensureSubjectPrefix,
    mergeConversations,
    getContactDetailDisplayData,
};
